import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { 
  Info, 
  Zap, 
  Search, 
  ThumbsUp, 
  BarChart, 
  Lock, 
  Sparkles, 
  Flag, 
  Globe, 
  FileText, 
  Clock, 
  Layers, 
  Shield, 
  Share2, 
  Code, 
  PenTool,
  Map,
  Smartphone,
  Repeat,
  BookOpen,
  HardDrive,
  Award,
  Cpu,
  ArrowUpRight,
  Video,
  Link,
  TrendingUp
} from "lucide-react";
import { Button } from "@/components/ui/button";

const seoTips = [
  {
    title: "AI Content Detection",
    message: "Search engines now use advanced AI to identify and prioritize authentic human-created content over AI-generated material.",
    icon: <Sparkles className="h-5 w-5" />,
    articleId: "content-optimization",
  },
  {
    title: "Voice Search Optimization",
    message: "By 2025, over 60% of searches are now voice-based. Optimize for conversational keywords and questions.",
    icon: <Info className="h-5 w-5" />,
    articleId: "voice-search-optimization",
  },
  {
    title: "Core Web Vitals 2.0",
    message: "Google's updated Core Web Vitals now include Interaction to Next Paint (INP) as a critical ranking signal.",
    icon: <Zap className="h-5 w-5" />,
    articleId: "technical-seo-mobile",
  },
  {
    title: "E-E-A-T Updates",
    message: "Google's E-E-A-T now puts double emphasis on Experience along with Expertise, Authoritativeness, and Trustworthiness.",
    icon: <ThumbsUp className="h-5 w-5" />,
    articleId: "content-quality-signals",
  },
  {
    title: "Hyper-Local SEO",
    message: "Neighborhood-level SEO is now essential, with 85% of local searches resulting in a visit within 24 hours.",
    icon: <Search className="h-5 w-5" />,
    articleId: "local-seo-guide",
  },
  {
    title: "Privacy-First Analytics",
    message: "With third-party cookies gone, first-party data collection and privacy-compliant analytics are crucial for SEO.",
    icon: <Lock className="h-5 w-5" />,
    articleId: "technical-page-analytics",
  },
  {
    title: "Video SEO",
    message: "Video search results now appear in 70% of SERPs, with engagement metrics directly impacting rankings.",
    icon: <BarChart className="h-5 w-5" />,
    articleId: "video-seo-strategy",
  },
  {
    title: "Zero-Click Optimization",
    message: "Position zero enhancements now allow featured snippets to include interactive elements with improved click-through rates.",
    icon: <Flag className="h-5 w-5" />,
    articleId: "featured-snippets-strategy",
  },
  {
    title: "Mobile-First Indexing",
    message: "Google now exclusively uses mobile-first indexing. Your mobile site is all that matters for rankings in 2025.",
    icon: <Smartphone className="h-5 w-5" />,
    articleId: "mobile-optimization",
  },
  {
    title: "Semantic Search Evolution",
    message: "Search engines now understand context and user intent at near-human levels. Focus on comprehensive topic coverage.",
    icon: <Globe className="h-5 w-5" />,
    articleId: "semantic-search",
  },
  {
    title: "Page Experience Metrics",
    message: "Google's page experience signals now account for approximately 30% of ranking factors. Optimize for speed and interactivity.",
    icon: <Zap className="h-5 w-5" />,
    articleId: "page-experience",
  },
  {
    title: "NLP Content Optimization",
    message: "Natural Language Processing advancements mean search engines can evaluate content quality with unprecedented accuracy.",
    icon: <FileText className="h-5 w-5" />,
    articleId: "content-quality",
  },
  {
    title: "Real-Time Indexing",
    message: "Google's instant indexing feature now allows important content to be indexed within minutes of publication.",
    icon: <Clock className="h-5 w-5" />,
    articleId: "indexing-strategy",
  },
  {
    title: "Visual Search Importance",
    message: "35% of searches now include visual components. Optimize your images with descriptive alt text and schema markup.",
    icon: <TrendingUp className="h-5 w-5" />,
    articleId: "visual-search",
  },
  {
    title: "Content Depth vs. Length",
    message: "Search engines now prioritize content depth over length. Comprehensive coverage of topics outperforms word count.",
    icon: <Layers className="h-5 w-5" />,
    articleId: "content-depth",
  },
  {
    title: "HTTPS Security Requirements",
    message: "Google now penalizes any site without HTTPS. Enhanced security features provide ranking boosts.",
    icon: <Shield className="h-5 w-5" />,
    articleId: "security-seo",
  },
  {
    title: "Social Signals Impact",
    message: "Social engagement metrics now directly influence search rankings. Build a cohesive social media strategy.",
    icon: <Share2 className="h-5 w-5" />,
    articleId: "social-seo",
  },
  {
    title: "Schema Markup Expansion",
    message: "New schema types provide enhanced SERP features. Implementing advanced structured data can increase CTR by 30%.",
    icon: <Code className="h-5 w-5" />,
    articleId: "schema-markup",
  },
  {
    title: "Image Optimization",
    message: "Next-gen image formats like WebP and AVIF can improve Core Web Vitals significantly. Convert and compress your images.",
    icon: <PenTool className="h-5 w-5" />,
    articleId: "image-optimization",
  },
  {
    title: "Geo-Targeting Precision",
    message: "Location-based search has evolved to block-level precision. Optimize for micro-geographic targeting.",
    icon: <Map className="h-5 w-5" />,
    articleId: "geo-targeting",
  },
  {
    title: "Entity-Based SEO",
    message: "Google's Knowledge Graph prioritizes entities over keywords. Build authoritative entity associations for your brand.",
    icon: <Repeat className="h-5 w-5" />,
    articleId: "entity-seo",
  },
  {
    title: "Professional Expertise Signals",
    message: "Author expertise is now a measurable ranking factor. Highlight credentials and professional experience.",
    icon: <BookOpen className="h-5 w-5" />,
    articleId: "author-expertise",
  },
  {
    title: "Progressive Web Apps",
    message: "PWAs now receive ranking boosts. Convert your site to a PWA for enhanced user experience and better SEO.",
    icon: <HardDrive className="h-5 w-5" />,
    articleId: "pwa-benefits",
  },
  {
    title: "Brand Authority Metrics",
    message: "Google now measures brand mentions even without links. Build brand recognition for improved rankings.",
    icon: <Award className="h-5 w-5" />,
    articleId: "brand-authority",
  },
  {
    title: "Site Architecture Importance",
    message: "Logical site structure and internal linking are now major ranking factors. Create clear content hierarchies.",
    icon: <Cpu className="h-5 w-5" />,
    articleId: "site-architecture",
  },
  {
    title: "Featured Snippet Optimization",
    message: "Position zero results now appear in over 40% of searches. Structure content to win these valuable placements.",
    icon: <ArrowUpRight className="h-5 w-5" />,
    articleId: "featured-snippets",
  },
  {
    title: "Video Transcript SEO",
    message: "Video transcript quality now affects ranking. Create detailed, accurate transcripts for all video content.",
    icon: <Video className="h-5 w-5" />,
    articleId: "video-transcripts",
  },
  {
    title: "Link Quality over Quantity",
    message: "Google's link evaluation systems now focus almost exclusively on quality and relevance rather than quantity.",
    icon: <Link className="h-5 w-5" />,
    articleId: "link-quality",
  },
];

const SeoTipsToaster = () => {
  const navigate = useNavigate();
  const [lastTipTime, setLastTipTime] = useState<number>(0);
  const [usedTips, setUsedTips] = useState<Set<number>>(new Set());

  const showRandomTip = () => {
    const now = Date.now();
    if (now - lastTipTime < 40000) return;
    
    let availableTips = seoTips;
    if (usedTips.size < seoTips.length) {
      availableTips = seoTips.filter((_, index) => !usedTips.has(index));
    }
    
    const randomIndex = Math.floor(Math.random() * availableTips.length);
    const randomTip = availableTips[randomIndex];
    
    const originalIndex = seoTips.findIndex(tip => tip.title === randomTip.title);
    
    setUsedTips(prev => {
      const updated = new Set(prev);
      updated.add(originalIndex);
      if (updated.size === seoTips.length) {
        return new Set();
      }
      return updated;
    });
    
    toast(
      <div>
        <div className="font-semibold flex items-center gap-2">
          {randomTip.icon}
          {randomTip.title}
        </div>
        <p className="mt-1">{randomTip.message}</p>
        <Button 
          variant="link" 
          className="mt-2 p-0 h-auto text-blue-500 hover:text-blue-700"
          onClick={() => navigate(`/knowledge-base/${randomTip.articleId}`)}
        >
          Learn more
        </Button>
      </div>
    );
    
    setLastTipTime(now);
  };

  useEffect(() => {
    const initialTimer = setTimeout(() => {
      showRandomTip();
    }, 10000);
    
    const intervalTimer = setInterval(() => {
      showRandomTip();
    }, 80000);
    
    return () => {
      clearTimeout(initialTimer);
      clearInterval(intervalTimer);
    };
  }, [lastTipTime, usedTips]);

  return null;
};

export default SeoTipsToaster;
